// Broadcast a message to all connected clients

import { getChannelsList } from '@/features/channels/channels.service';
import { Message } from '@/features/messages/messages.types';
import { supabase } from '@/services/supabase-client';
import { REALTIME_SUBSCRIBE_STATES } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

interface MessageBroadcastPayload {
  conversation_id: string;
  id: string;
  content: string;
  sender_id: string;
  created_at: string;
  type: 'message';
  // Include all other Message properties as they might be part of the payload
}

interface MessagesPageData {
  data: Message[];
  page: number;
  hasMore: boolean;
  total: number;
}

export default function useBroadcast() {
  const queryClient = useQueryClient();

  const handleMessageBroadcast = useCallback(
    async (message_payload: {
      event: 'message';
      payload: MessageBroadcastPayload;
      type: 'broadcast';
    }) => {
      console.log(
        '[useBroadcast] received broadcast message 👀',
        message_payload,
      );
      // Extract conversation_id and new message from payload
      const conversationId = message_payload?.payload?.conversation_id;
      const newMessage = message_payload?.payload;

      const queryKey = ['messages', 'infinite', conversationId];
      await queryClient.invalidateQueries({ queryKey });
      const conversarionsKey = ['conversations'];
      await queryClient.invalidateQueries({ queryKey: conversarionsKey });

      // Add the new message to the query client
      // queryClient.setQueryData(
      //   queryKey,
      //   (
      //     oldData:
      //       | { pages: MessagesPageData[]; pageParams: (number | undefined)[] }
      //       | undefined,
      //   ) => {
      //     console.log('[useBroadcast] oldData', oldData);

      //     if (!oldData || !oldData.pages || oldData.pages.length === 0) {
      //       console.log(
      //         '[useBroadcast] No existing data, creating new structure',
      //       );
      //       return {
      //         pages: [
      //           {
      //             data: [newMessage],
      //             page: 1,
      //             hasMore: false,
      //             total: 1,
      //           },
      //         ],
      //         pageParams: [undefined],
      //       };
      //     }

      //     // Check if message already exists (avoid duplicates)
      //     const messageExists = oldData.pages.some((page: MessagesPageData) =>
      //       page.data?.some((msg: Message) => msg.id === newMessage.id),
      //     );

      //     // if (messageExists) {
      //     //   console.log('[useBroadcast] Message already exists, skipping');
      //     //   return oldData;
      //     // }

      //     // Add new message to the first page (most recent)
      //     const updatedPages = oldData.pages.map(
      //       (page: MessagesPageData, index: number) => {
      //         if (index === 0) {
      //           // Add new message at the beginning of the first page
      //           return {
      //             ...page,
      //             data: [
      //               { ...newMessage, id: `${newMessage.id}-${Date.now()}` },
      //               ...page.data,
      //             ],
      //             total: (page.total || 0) + 1,
      //           };
      //         }
      //         return page;
      //       },
      //     );

      //     console.log('[useBroadcast] updatedPages', updatedPages);

      //     return {
      //       ...oldData,
      //       pages: updatedPages,
      //     };
      //   },
      // );
    },
    [queryClient],
  );

  useEffect(() => {
    const setupRealtimeListener = async () => {
      try {
        await supabase.realtime.setAuth();

        const response = await getChannelsList({});

        console.log('[useBroadcast] response', response);
        const channels = response.data;
        if (channels.length === 0) {
          console.log('[useBroadcast] no channels found');
          return;
        }

        console.log('[useBroadcast] channels', channels);

        // Subscribe into all channels
        const supabaseChannels = channels.map((c) => {
          const channelName = `channel:${c.id}`;
          const channel = supabase.channel(channelName, {
            config: {
              broadcast: { self: true },
            },
          });

          // TODO: Subscribe into some channels whatsapp etc
          channel
            .on(
              // @ts-expect-error - see https://supabase.com/docs/guides/realtime/callbacks/broadcast-handler
              'broadcast',
              { event: 'message' },
              handleMessageBroadcast,
            )
            .subscribe((status) => {
              console.log(
                `[useBroadcast] channel ${channelName} status`,
                status,
              );
              if (status === REALTIME_SUBSCRIBE_STATES.TIMED_OUT) {
                console.log('[useBroadcast] channel is timed out');
              }

              if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
                console.log(`[useBroadcast] channel IS SUBSCRIBED`);
              }
              if (status === REALTIME_SUBSCRIBE_STATES.CLOSED) {
                console.log(`[useBroadcast] channel is CLOSED`);
              }
              if (status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR) {
                console.log(`[useBroadcast] channel is CHANNEL_ERROR`);
              }
            });

          return channel;
        });

        return () => {
          console.log('[useBroadcast] CLEANUP');
          supabaseChannels.forEach((c) => {
            supabase.removeChannel(c);
          });
        };
      } catch (error) {
        console.error(
          `[useBroadcast] Error setting up realtime listener for chat`,
          error,
        );
      }
    };

    const cleanup = setupRealtimeListener();

    return () => {
      cleanup?.then((cleanupFn) => cleanupFn?.());
    };
  }, []);
}
